/**
 * @file Go API (berry-buds-api) への薄い fetch ラッパ。
 *
 * @what: `VITE_API_BASE_URL` を基点に、Firebase ID token を Bearer で付けて JSON を
 *   やり取りする。レスポンスが 2xx 以外なら `ApiError` を throw する。
 * @why: web からの Go API 呼び出しはいちごスタディ (epic_23) が初出。Firestore 直読み
 *   ではなく backend API 経由に統一する既存規約 (書込は全て API、rules は read gate)
 *   に web も乗せる。認証ヘッダの付け方を 1 箇所に閉じ、feature 側は path と型だけ
 *   を意識する。
 * @failure:
 *   - 未ログイン (currentUser 無し) は `ApiError(401)` を throw (呼び出し側は AuthGate 配下
 *     で使う前提なので通常到達しない)
 *   - network 失敗は fetch の TypeError をそのまま伝播
 *   - 2xx 以外は `ApiError` (status と、あれば JSON body の message)。UI には内部詳細を
 *     出さず、console に残す (feedback_no_internal_errors_in_ui)
 */

import { getFirebaseAuth } from "../firebase/client";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown = undefined) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function apiBaseUrl(): string {
  const raw = import.meta.env.VITE_API_BASE_URL;
  if (typeof raw !== "string" || raw.length === 0) {
    throw new Error("Missing required env var: VITE_API_BASE_URL");
  }
  return raw.replace(/\/+$/, "");
}

export type ApiRequest = {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** ページ離脱中でも送信を続ける (きき分けの途中離脱保存)。body 64KB 未満のときだけ有効。 */
  keepalive?: boolean;
};

async function bearerToken(): Promise<string> {
  const user = getFirebaseAuth().currentUser;
  if (!user) {
    throw new ApiError(401, "not signed in");
  }
  return user.getIdToken();
}

/**
 * JSON API を叩く。`path` は `/me/families` のように先頭スラッシュ付きで渡す。
 * 204 / 空 body は `undefined` を返す。
 */
export async function apiFetch<T>(path: string, req: ApiRequest = {}): Promise<T> {
  const token = await bearerToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  const init: RequestInit = { method: req.method ?? "GET", headers };
  if (req.signal) init.signal = req.signal;
  if (req.keepalive) init.keepalive = true;
  if (req.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(req.body);
  }

  const res = await fetch(`${apiBaseUrl()}${path}`, init);
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    const message =
      typeof parsed === "object" && parsed !== null && "message" in parsed
        ? String((parsed as { message: unknown }).message)
        : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, message, parsed);
  }
  return parsed as T;
}

/** SSE の1つの出来事である (`event:` と `data:`)。 */
export type ApiStreamEvent = { name: string; data: string };

/**
 * Server-Sent Events を受ける (POST で依頼し、答えを少しずつ受け取る)。
 *
 * @what: apiFetch と同じ認証と基点で叩き、出来事が届くたびに onEvent を呼ぶ。2xx でなければ
 *   apiFetch と同じ ApiError を throw する (本文が JSON ならその message を持つ)。
 * @why: AI の答えは十数秒かかる。1回で受け取ると、その間ずっと何も出ない。
 * @failure: 応答が本体を持たないとき、途中で切れたときは ApiError を throw する。
 */
export async function apiEventStream(
  path: string,
  req: { body?: unknown; signal?: AbortSignal },
  onEvent: (event: ApiStreamEvent) => void,
): Promise<void> {
  const token = await bearerToken();
  const headers: Record<string, string> = {
    Accept: "text/event-stream",
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const init: RequestInit = { method: "POST", headers, body: JSON.stringify(req.body ?? {}) };
  if (req.signal) init.signal = req.signal;
  const res = await fetch(`${apiBaseUrl()}${path}`, init);
  if (!res.ok) {
    const text = await res.text();
    let parsed: unknown = undefined;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }
    const message =
      typeof parsed === "object" && parsed !== null && "message" in parsed
        ? String((parsed as { message: unknown }).message)
        : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, message, parsed);
  }
  if (res.body === null) {
    throw new ApiError(res.status, "no stream body");
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // 出来事は空行で区切られる。最後の1つは途中かもしれないので残す。
    const blocks = buffer.split("\n\n");
    buffer = blocks.pop() ?? "";
    for (const block of blocks) {
      const event = parseStreamBlock(block);
      if (event !== null) onEvent(event);
    }
  }
  const last = parseStreamBlock(buffer);
  if (last !== null) onEvent(last);
}

/** 1つの区切りを出来事に直す (名前が無ければ null)。 */
function parseStreamBlock(block: string): ApiStreamEvent | null {
  let name = "";
  const data: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) name = line.slice("event:".length).trim();
    else if (line.startsWith("data:")) data.push(line.slice("data:".length).trim());
  }
  if (name === "") return null;
  return { name, data: data.join("\n") };
}
